/**
 * Usage Overview Card Component
 * 
 * Main dashboard component showing user's usage summary including:
 * - Free daily/weekly usage with progress bars
 * - Credit balances
 * - Low usage warnings
 * - Purchase credit button
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Search, Brain, Plus, Gift, Crown, AlertTriangle, Clock } from 'lucide-react';
import type { UsageSummary } from '../../types/usage';

interface UsageOverviewCardProps {
  usage: UsageSummary;
  onPurchaseClick: () => void;
  className?: string;
}

export function UsageOverviewCard({ 
  usage, 
  onPurchaseClick, 
  className = "" 
}: UsageOverviewCardProps) {
  // Calculate progress percentages
  const searchProgress = usage.daily_searches_remaining > 0 
    ? ((5 - usage.daily_searches_remaining) / 5) * 100 
    : 100;
    
  const aiProgress = usage.weekly_ai_analyses_remaining > 0
    ? ((1 - usage.weekly_ai_analyses_remaining) / 1) * 100
    : 100;

  // Determine warning states
  const hasLowSearchUsage = usage.daily_searches_remaining <= 1 && !usage.is_owner;
  const hasLowAiUsage = usage.weekly_ai_analyses_remaining === 0 && !usage.is_owner;
  const hasCredits = usage.search_credits > 0 || usage.ai_analysis_credits > 0;
  const isOutOfUsage = hasLowSearchUsage && hasLowAiUsage && !hasCredits && !usage.is_owner;

  // Owner display
  if (usage.is_owner) {
    return (
      <Card className={`w-full ${className}`}>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-primary" />
              <span>Owner Access</span>
            </div>
            <Badge variant="secondary" className="bg-secondary text-foreground">
              Unlimited
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center py-6">
            <Crown className="w-12 h-12 text-primary mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Unlimited Access
            </h3>
            <p className="text-sm text-muted-foreground">
              You have unlimited searches and AI analysis as a business owner.
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4 pt-4 border-t">
            <div className="text-center">
              <div className="text-sm text-muted-foreground">Total Searches</div>
              <div className="text-lg font-semibold">{usage.total_search_credits_used}</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-muted-foreground">Total AI Analysis</div>
              <div className="text-lg font-semibold">{usage.total_ai_analysis_credits_used}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`w-full ${className}`}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Usage Overview</span>
          <Button onClick={onPurchaseClick} size="sm" className="gap-2">
            <Plus className="w-4 h-4" />
            Buy Credits
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Critical Usage Alert */}
        {isOutOfUsage && (
          <Alert className="border-red-200 bg-red-50">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <AlertDescription className="text-red-700">
              You've reached your usage limits and have no credits remaining. 
              Purchase credits to continue or wait for your free usage to reset.
            </AlertDescription>
          </Alert>
        )}

        {/* Free Usage Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-3">
            <Gift className="w-4 h-4 text-green-600" />
            <span className="font-medium text-green-600">Free Daily/Weekly Allowance</span>
            <Badge variant="outline" className="text-xs bg-green-50 border-green-200">
              Resets automatically
            </Badge>
          </div>
          
          {/* Daily Searches */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium">Daily Searches</span>
              </div>
              <Badge 
                variant={usage.daily_searches_remaining > 1 ? "default" : "destructive"}
                className="text-xs"
              >
                {usage.daily_searches_remaining} remaining today
              </Badge>
            </div>
            <Progress 
              value={searchProgress} 
              className="h-2"
              style={{
                backgroundColor: usage.daily_searches_remaining <= 1 ? '#fee2e2' : undefined
              }}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Used {usage.daily_searches_used} of 5 free searches today</span>
              {hasLowSearchUsage && (
                <div className="flex items-center gap-1 text-amber-600">
                  <Clock className="w-3 h-3" />
                  <span>Resets in ~14h</span>
                </div>
              )}
            </div>
          </div>

          {/* Weekly AI Analysis */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Brain className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium">Weekly AI Analysis</span>
              </div>
              <Badge 
                variant={usage.weekly_ai_analyses_remaining > 0 ? "default" : "destructive"}
                className="text-xs"
              >
                {usage.weekly_ai_analyses_remaining} remaining this week
              </Badge>
            </div>
            <Progress 
              value={aiProgress} 
              className="h-2"
              style={{
                backgroundColor: usage.weekly_ai_analyses_remaining === 0 ? '#fee2e2' : undefined
              }}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Used {usage.weekly_ai_analyses_used} of 1 free analysis this week</span>
              {hasLowAiUsage && (
                <div className="flex items-center gap-1 text-amber-600">
                  <Clock className="w-3 h-3" />
                  <span>Resets Monday</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Credit Balance Section */}
        {hasCredits && (
          <div className="space-y-4 border-t pt-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="font-medium text-blue-600">Purchased Credits</span>
              <Badge variant="secondary" className="text-xs">
                Never expire
              </Badge>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 bg-blue-50 rounded-lg border">
                <div className="text-2xl font-bold text-blue-600">
                  {usage.search_credits}
                </div>
                <div className="text-xs text-blue-600">Search Credits</div>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg border">
                <div className="text-2xl font-bold text-blue-600">
                  {usage.ai_analysis_credits}
                </div>
                <div className="text-xs text-blue-600">AI Analysis Credits</div>
              </div>
            </div>
          </div>
        )}

        {/* Low Usage Warning */}
        {(hasLowSearchUsage || hasLowAiUsage) && !isOutOfUsage && (
          <Alert className="border-amber-200 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <AlertDescription className="text-amber-800">
              {hasLowSearchUsage && hasLowAiUsage 
                ? "You're running low on both free searches and AI analysis."
                : hasLowSearchUsage 
                ? "You have only 1 free search remaining today."
                : "You've used your free AI analysis for this week."
              }
              {!hasCredits && " Consider purchasing credits to continue without interruption."}
            </AlertDescription>
          </Alert>
        )}

        {/* No Credits Prompt */}
        {!hasCredits && !isOutOfUsage && (
          <div className="p-3 bg-gray-50 rounded-lg border border-dashed">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-2">
                Need more usage? Purchase credits for unlimited access.
              </p>
              <Button 
                onClick={onPurchaseClick} 
                size="sm" 
                variant="outline"
                className="gap-2"
              >
                <Plus className="w-3 h-3" />
                View Credit Packages
              </Button>
            </div>
          </div>
        )}

        {/* Usage Summary Stats */}
        <div className="text-xs text-muted-foreground bg-gray-50 p-3 rounded-lg">
          <div className="text-center font-medium mb-2">All-Time Usage Statistics</div>
          <div className="grid grid-cols-2 gap-2">
            <div className="text-center">
              <div className="font-medium">{usage.total_search_credits_used}</div>
              <div>Total searches performed</div>
            </div>
            <div className="text-center">
              <div className="font-medium">{usage.total_ai_analysis_credits_used}</div>
              <div>Total AI analysis completed</div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}